import EventEmitter from 'eventemitter3';
import { EventTypes } from '../core/event-bus';

import { IUser, IUserLoyaltyReward, IUserCampaignCodesData, IUserNextOrderCredit } from './user.interface';

/**
 * Represents a user's profile with favorite stores, loyalty info and login status.
 */
export class User {
    private readonly _eventBus: EventEmitter;
    private _user: IUser;

    /**
     * @private
     * @property {boolean} _loggedIn - A boolean value indicating whether the user is logged in or not.
     */
    private _loggedIn: boolean = false;

    constructor(eventBus: EventEmitter) {
        this._user = {} as IUser;
        this._eventBus = eventBus;
    }

    set user(user: IUser) {
        this._user = user;
    }

    get user(): IUser {
        return { ...this._user } as IUser
    }

    public getProperty<K extends keyof IUser>(property: K): IUser[K] | null {
        if (!this._user)
            return null;

        return this._user[property];
    }

    public setProperty<K extends keyof IUser>(key: K, value: IUser[K]) {
        this._user[key] = value;
    }

    public updateUser(updates: Partial<IUser>): void {
        if (!this._user)
            return;

        this._user = { ...this._user, ...updates };
    }

    public setLoggedIn(loggedIn: boolean) {
        this._loggedIn = loggedIn;

        if (this._loggedIn)
            this._eventBus.emit(EventTypes.LOGGED_IN);
        else
            this._eventBus.emit(EventTypes.LOGGED_OUT);
    }

    /**
     * Checks whether the user is currently logged in.
     * @returns {boolean} True if the user is logged in, false otherwise.
     */
    public isLoggedIn = (): boolean => {
        return this._loggedIn;
    }

    /**
     *  Gets the user's reward info for the given reward id
     * @param rewardId - ID of the reward
     * @returns {IUserLoyaltyReward | null} - The reward object if found, null otherwise}
     */
    public getRewardInfo = (rewardId: number): IUserLoyaltyReward | null => {
        const userLoyalty = this._user.LoyaltyData;
        if (!userLoyalty || userLoyalty.Rewards.length == 0) {
            return null;
        }

        const rewards = userLoyalty.Rewards.filter((reward: IUserLoyaltyReward) => !reward.Expired);
        return rewards.find((x: IUserLoyaltyReward) => x.RewardId === rewardId) || null;
    }

    /**
     * Gets the user's offer info for the given coupon code
     * @param couponCode - Coupon code of the reward
     * @returns {IUserCampaignCodesData | null} - The offer object if found, null otherwise
     */
    public getOfferInfo = (couponCode: string): IUserCampaignCodesData | null => {
        const offers = this._user.Offers;
        if (!offers || offers.Codes.length == 0) {
            return null;
        }

        return offers.Codes.find(offer => offer.CouponCode.toLowerCase() == couponCode.toLowerCase()) || null;
    }

    /**
     * Gets the user's next order credit info for the given customer credit ID
     * @param creditId - The customer credit ID for the next order credit
     * @returns {IUserNextOrderCredit | null} - The credit object if found, null otherwise
     */
    public getCreditInfo = (creditId: string): IUserNextOrderCredit | null => {
        const userCredits = this._user.NextOrderCredits;
        if (!userCredits || userCredits.length === 0) {
            return null;
        }

        return userCredits.find((x: IUserNextOrderCredit) => x.CustomerCreditID === Number(creditId)) || null;
    }


    public setDefaultStore(storeId: number){
        if (this._user.FavoriteStoreId)
            return;

        this._user.FavoriteStoreId = storeId;
        this._eventBus.emit(EventTypes.USER_CHANGED);
    }

    /**
     * Removes rewards from the user's loyalty data when their `RewardId`
     * exists in the provided list of redeemed reward IDs.
     *
     * @param redeemedRewardIds - Reward IDs that have already been redeemed.
     */
    public removeUsedRewards(redeemedRewardIds: number[]): void {
        const loyaltyData = this._user.LoyaltyData;

        if (!loyaltyData?.Rewards?.length || redeemedRewardIds.length === 0)
            return;

        const redeemedRewardIdSet = new Set(redeemedRewardIds);

        loyaltyData.Rewards = loyaltyData.Rewards.filter(
            reward => !redeemedRewardIdSet.has(reward.RewardId)
        );
    }
}